import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Copy, RefreshCw, Trash2, Eye, EyeOff, KeyRound, Link2, AlertTriangle,
  Star, EyeOff as Hide, Rss, Calendar, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';
import { api } from '../../utils/api';
import { useConfirm } from '../ConfirmProvider';

// ---- small helpers ----
const REGIONS = [
  { value: 'eu', label: 'EU (eu.nexuscms.ai)' },
  { value: 'us', label: 'US (us.nexuscms.ai)' },
  { value: 'au', label: 'AU (au.nexuscms.ai)' },
  { value: 'ap', label: 'AP (ap.nexuscms.ai)' },
];

function ArticleCurationRow({ article, onToggle }) {
  const [busy, setBusy] = useState(false);

  const toggle = async (field) => {
    setBusy(true);
    await onToggle(article.id, field, !article[field]);
    setBusy(false);
  };

  return (
    <div
      data-testid={`nexus-article-row-${article.id}`}
      className={`flex items-start gap-3 p-3 rounded-lg border transition-opacity ${article.hidden ? 'opacity-50' : ''}`}
    >
      {article.image_url && (
        <img src={article.image_url} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{article.title}</p>
        <p className="text-xs text-muted-foreground line-clamp-1">{article.excerpt}</p>
        <div className="flex gap-1 mt-1 flex-wrap">
          {(article.categories || []).map(c => (
            <Badge key={c} variant="secondary" className="text-xs py-0">{c}</Badge>
          ))}
        </div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <Button
          size="icon"
          variant={article.starred ? 'default' : 'outline'}
          className={`w-7 h-7 ${article.starred ? 'bg-amber-400 hover:bg-amber-500 border-amber-400' : ''}`}
          onClick={() => toggle('starred')}
          disabled={busy}
          title={article.starred ? 'Unfeature' : 'Feature (star)'}
          data-testid={`nexus-star-${article.id}`}
        >
          <Star className="w-3 h-3" />
        </Button>
        <Button
          size="icon"
          variant={article.hidden ? 'destructive' : 'outline'}
          className="w-7 h-7"
          onClick={() => toggle('hidden')}
          disabled={busy}
          title={article.hidden ? 'Show' : 'Hide from PWA'}
          data-testid={`nexus-hide-${article.id}`}
        >
          <Hide className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export default function NexusCmsIntegration({ customer, onCustomerUpdate }) {
  const confirm = useConfirm();
  // API key state
  const [keyStatus, setKeyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealedKey, setRevealedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // Short code state
  const [shortCode, setShortCode] = useState(customer?.short_code || '');
  const [savingShortCode, setSavingShortCode] = useState(false);

  // Nexus App-Feed settings
  const [nexusTenantId, setNexusTenantId] = useState(customer?.nexus_tenant_id || '');
  const [nexusApiKey, setNexusApiKey] = useState(customer?.nexus_api_key || '');
  const [showNexusKey, setShowNexusKey] = useState(false);
  const [nexusRegion, setNexusRegion] = useState(customer?.nexus_region || 'eu');
  const [nexusArticlesEnabled, setNexusArticlesEnabled] = useState(customer?.nexus_articles_enabled || false);
  const [nexusEventsEnabled, setNexusEventsEnabled] = useState(customer?.nexus_events_enabled || false);
  const [savingNexus, setSavingNexus] = useState(false);
  const [detectingRegion, setDetectingRegion] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Articles curation state
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);

  useEffect(() => {
    setShortCode(customer?.short_code || '');
    setNexusTenantId(customer?.nexus_tenant_id || '');
    setNexusApiKey(customer?.nexus_api_key || '');
    setNexusRegion(customer?.nexus_region || 'eu');
    setNexusArticlesEnabled(customer?.nexus_articles_enabled || false);
    setNexusEventsEnabled(customer?.nexus_events_enabled || false);
  }, [customer]);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const status = await api.getApiKeyStatus();
      setKeyStatus(status);
    } catch (err) {
      console.error('Failed to fetch API key status', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchArticles = useCallback(async () => {
    if (!nexusTenantId || !nexusApiKey) return;
    setArticlesLoading(true);
    try {
      const data = await api.nexusListArticles({ limit: 50 });
      setArticles(data.articles || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not fetch Nexus articles');
    } finally {
      setArticlesLoading(false);
    }
  }, [nexusTenantId, nexusApiKey]);

  const handleToggleArticlesPanel = () => {
    const next = !articlesOpen;
    setArticlesOpen(next);
    if (next && articles.length === 0) fetchArticles();
  };

  const handleCurate = async (articleId, field, value) => {
    try {
      await api.nexusCurateArticle(articleId, { [field]: value });
      setArticles(prev =>
        prev.map(a => a.id === articleId ? { ...a, [field]: value } : a)
      );
    } catch (err) {
      toast.error('Could not update curation');
    }
  };

  const handleGenerate = async () => {
    if (keyStatus?.has_api_key) {
      const ok = await confirm({
        title: 'Generate a new API key?',
        description: 'The existing key will stop working immediately.',
        confirmText: 'Generate new key',
      });
      if (!ok) return;
    }
    setGenerating(true);
    try {
      const { api_key } = await api.generateApiKey();
      setRevealedKey(api_key);
      setShowKey(true);
      toast.success('API key generated. Copy it now — you will not see it again.');
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async () => {
    const ok = await confirm({
      title: 'Revoke API key?',
      description: 'The current key will be disabled immediately.',
      confirmText: 'Revoke key',
    });
    if (!ok) return;
    setRevoking(true);
    try {
      await api.revokeApiKey();
      setRevealedKey(null);
      toast.success('API key revoked');
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke API key');
    } finally {
      setRevoking(false);
    }
  };

  const handleCopy = (text, label = 'Copied') => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(label),
      () => toast.error('Copy failed — please select and copy manually')
    );
  };

  const handleSaveShortCode = async () => {
    const code = shortCode.trim();
    if (code && !/^[a-zA-Z0-9-]{2,30}$/.test(code)) {
      toast.error('Short code must be 2–30 letters, numbers, or hyphens.');
      return;
    }
    setSavingShortCode(true);
    try {
      await api.updateCustomer(customer.id, { short_code: code || null });
      toast.success('Short code saved');
      onCustomerUpdate && onCustomerUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save short code');
    } finally {
      setSavingShortCode(false);
    }
  };

  const handleSaveNexus = async () => {
    setSavingNexus(true);
    try {
      let region = nexusRegion;
      // Admins shouldn't have to know which Nexus server they're on — find it for them.
      if (nexusTenantId.trim() && nexusApiKey.trim()) {
        const detected = await detectRegion({ silent: true });
        if (detected) region = detected;
      }
      await api.updateCustomer(customer.id, {
        nexus_tenant_id: nexusTenantId.trim() || null,
        nexus_api_key: nexusApiKey.trim() || null,
        nexus_region: region,
        nexus_articles_enabled: nexusArticlesEnabled,
        nexus_events_enabled: nexusEventsEnabled,
      });
      toast.success('Nexus App-Feed settings saved');
      onCustomerUpdate && onCustomerUpdate();
      // Refresh articles if panel open
      if (articlesOpen) fetchArticles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Nexus settings');
    } finally {
      setSavingNexus(false);
    }
  };

  const detectRegion = async ({ silent = false } = {}) => {
    if (!nexusTenantId.trim() || !nexusApiKey.trim()) {
      if (!silent) toast.error('Enter your Nexus Tenant ID and API key first');
      return null;
    }
    setDetectingRegion(true);
    try {
      const res = await api.detectNexusRegion({
        nexus_tenant_id: nexusTenantId.trim(),
        nexus_api_key: nexusApiKey.trim(),
      });
      if (res?.detected) {
        setNexusRegion(res.nexus_region);
        if (!silent) toast.success(`Found your Nexus server: ${res.nexus_region.toUpperCase()}`);
        return res.nexus_region;
      }
      if (!silent) toast.error('Could not reach Nexus with those details — check the Tenant ID and key');
      return null;
    } catch {
      if (!silent) toast.error('Region detection failed');
      return null;
    } finally {
      setDetectingRegion(false);
    }
  };

  const nexusDirty =
    nexusTenantId !== (customer?.nexus_tenant_id || '') ||
    nexusApiKey !== (customer?.nexus_api_key || '') ||
    nexusRegion !== (customer?.nexus_region || 'eu') ||
    nexusArticlesEnabled !== (customer?.nexus_articles_enabled || false) ||
    nexusEventsEnabled !== (customer?.nexus_events_enabled || false);

  // Integration URLs must always show the canonical public domain, never the
  // infrastructure host the admin happens to be loaded from (e.g. inside the
  // NexusCMS iframe, window.location.host is tileboard.emergent.host).
  const apiBase = 'https://taptile.app';
  const nexusConfigured = nexusApiKey && nexusTenantId && !nexusDirty;
  const autoProvisioned = customer?.provisioned_by === 'nexus';

  return (
    <div className="space-y-4">
      {autoProvisioned && (
        <Card className="border-emerald-200 bg-emerald-50/60" data-testid="nexus-auto-provisioned-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
              <Link2 className="w-4 h-4" />
              Connected to NexusCMS — configured automatically
            </CardTitle>
            <CardDescription className="text-xs text-emerald-800">
              This workspace was created from NexusCMS. Your Tenant ID, API keys and server region were
              all set up for you — there is nothing to copy, paste or configure here.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-emerald-700">Nexus Tenant ID</p>
                <p className="font-mono text-emerald-950 truncate" data-testid="nexus-auto-tenant-id">{nexusTenantId || '—'}</p>
              </div>
              <div>
                <p className="text-emerald-700">Nexus server</p>
                <p className="font-mono text-emerald-950" data-testid="nexus-auto-region">{(nexusRegion || 'eu').toUpperCase()}</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-xs text-emerald-900 h-7 px-2"
              onClick={() => setShowAdvanced(!showAdvanced)}
              data-testid="nexus-advanced-toggle"
            >
              {showAdvanced ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
              {showAdvanced ? 'Hide advanced settings' : 'Advanced settings'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Short Code Card */}
      <Card data-testid="short-code-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Short Code (iOS App Login)
          </CardTitle>
          <CardDescription className="text-xs">
            Give your users a memorable code (e.g. <code className="bg-gray-100 px-1 rounded">STANDREWS</code>) instead of a long ID when they log in to the iOS app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              data-testid="short-code-input"
              value={shortCode}
              onChange={(e) => setShortCode(e.target.value.toUpperCase())}
              placeholder="e.g. STANDREWS"
              maxLength={30}
              className="font-mono uppercase"
            />
            <Button
              data-testid="short-code-save-btn"
              onClick={handleSaveShortCode}
              disabled={savingShortCode || shortCode === (customer?.short_code || '')}
            >
              {savingShortCode ? 'Saving…' : 'Save'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Use only letters, numbers, and hyphens (2–30 characters). Case-insensitive when used.
          </p>
        </CardContent>
      </Card>

      {/* API Key Card */}
      {(!autoProvisioned || showAdvanced) && (
      <Card data-testid="nexuscms-api-key-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4" />
            External System API Key
          </CardTitle>
          <CardDescription className="text-xs">
            This key lets NexusCMS and other external systems sync contacts and access the App-Feed.
            Treat it like a password.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border">
                <div>
                  <p className="text-sm font-medium">
                    {keyStatus?.has_api_key ? 'API key is active' : 'No API key set'}
                  </p>
                  {keyStatus?.has_api_key && keyStatus?.created_at && (
                    <p className="text-xs text-muted-foreground">
                      Created {new Date(keyStatus.created_at).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="generate-api-key-btn"
                    onClick={handleGenerate} disabled={generating}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${generating ? 'animate-spin' : ''}`} />
                    {keyStatus?.has_api_key ? 'Regenerate' : 'Generate'}
                  </Button>
                  {keyStatus?.has_api_key && (
                    <Button size="sm" variant="outline" data-testid="revoke-api-key-btn"
                      className="text-red-600 hover:text-red-700" onClick={handleRevoke} disabled={revoking}>
                      <Trash2 className="w-3 h-3 mr-1" />
                      {revoking ? 'Revoking…' : 'Revoke'}
                    </Button>
                  )}
                </div>
              </div>

              {revealedKey && (
                <Alert variant="default" className="border-amber-300 bg-amber-50">
                  <AlertTriangle className="w-4 h-4 text-amber-700" />
                  <AlertDescription className="space-y-2">
                    <p className="text-xs font-medium text-amber-900">
                      Copy this key now — for security, it will <strong>not be shown again</strong>:
                    </p>
                    <div className="flex gap-2">
                      <Input data-testid="revealed-api-key" readOnly
                        type={showKey ? 'text' : 'password'} value={revealedKey}
                        className="font-mono text-xs bg-white" />
                      <Button size="icon" variant="outline" type="button"
                        onClick={() => setShowKey(!showKey)} aria-label={showKey ? 'Hide key' : 'Show key'}>
                        {showKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      </Button>
                      <Button size="icon" variant="outline" type="button"
                        data-testid="copy-api-key-btn" onClick={() => handleCopy(revealedKey, 'API key copied')}
                        aria-label="Copy key">
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {keyStatus?.has_api_key && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Contact sync endpoint (NexusCMS → Taptile)</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={`${apiBase}/api/v1/contacts`} className="font-mono text-xs" />
                    <Button size="icon" variant="outline"
                      onClick={() => handleCopy(`${apiBase}/api/v1/contacts`, 'URL copied')}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Nexus App-Feed Settings */}
      <Card data-testid="nexus-appfeed-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Rss className="w-4 h-4" />
            Nexus App-Feed
          </CardTitle>
          <CardDescription className="text-xs">
            {autoProvisioned
              ? 'Articles and events are pulled straight from NexusCMS using the credentials set up for you. Use the toggles below to choose what appears in your portal.'
              : 'Pull articles and events directly from NexusCMS into your portal. Enter your Nexus Tenant ID (found in Nexus → Settings → General) and choose which features to enable.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!nexusTenantId && (
            <Alert variant="default" className="border-blue-200 bg-blue-50">
              <AlertTriangle className="w-4 h-4 text-blue-700" />
              <AlertDescription className="text-xs text-blue-800">
                Enter your Nexus Tenant ID and API Key below, then enable Articles or Events to connect.
                We'll work out which Nexus server you're on for you.
              </AlertDescription>
            </Alert>
          )}

          {(!autoProvisioned || showAdvanced) && (
          <>
          <div className="space-y-1">
            <Label className="text-xs font-medium">Nexus Tenant ID</Label>
            <Input
              data-testid="nexus-tenant-id-input"
              value={nexusTenantId}
              onChange={(e) => setNexusTenantId(e.target.value)}
              placeholder="e.g. 4c7cf0af-4073-43c7-81ac-677043c80980"
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">Found in Nexus → Settings → General</p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium">Nexus API Key</Label>
            <div className="flex gap-2">
              <Input
                data-testid="nexus-api-key-input"
                type={showNexusKey ? 'text' : 'password'}
                value={nexusApiKey}
                onChange={(e) => setNexusApiKey(e.target.value)}
                placeholder="Paste your Nexus API key here"
                className="font-mono text-xs flex-1"
              />
              <Button
                size="icon"
                variant="outline"
                type="button"
                onClick={() => setShowNexusKey(!showNexusKey)}
                aria-label={showNexusKey ? 'Hide key' : 'Show key'}
                data-testid="nexus-api-key-toggle-btn"
              >
                {showNexusKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Found in NexusCMS → Settings → Integrations → API Key</p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium">Nexus Server Region</Label>
            <div className="flex gap-2">
              <select
                data-testid="nexus-region-select"
                value={nexusRegion}
                onChange={(e) => setNexusRegion(e.target.value)}
                className="flex-1 border rounded-md px-3 py-2 text-sm bg-white"
              >
                {REGIONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                onClick={() => detectRegion()}
                disabled={detectingRegion}
                data-testid="nexus-detect-region-btn"
              >
                {detectingRegion ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Detect'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Detected automatically when you save — you only need this if detection fails.
            </p>
          </div>
          </>
          )}

          <div className="space-y-3 pt-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium flex items-center gap-2">
                  <Rss className="w-3.5 h-3.5" /> Enable Nexus Articles
                </p>
                <p className="text-xs text-muted-foreground">Show Nexus news articles in the PWA feed</p>
              </div>
              <Switch
                data-testid="nexus-articles-toggle"
                checked={nexusArticlesEnabled}
                onCheckedChange={setNexusArticlesEnabled}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" /> Enable Nexus Events
                </p>
                <p className="text-xs text-muted-foreground">Show events calendar and RSVP in the PWA</p>
              </div>
              <Switch
                data-testid="nexus-events-toggle"
                checked={nexusEventsEnabled}
                onCheckedChange={setNexusEventsEnabled}
              />
            </div>
          </div>

          <Button
            data-testid="nexus-appfeed-save-btn"
            onClick={handleSaveNexus}
            disabled={savingNexus || !nexusDirty}
            className="w-full"
          >
            {savingNexus ? 'Saving…' : 'Save App-Feed Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Articles Curation Panel */}
      {nexusConfigured && nexusArticlesEnabled && (
        <Card data-testid="nexus-articles-curation-card">
          <CardHeader className="pb-3">
            <div
              role="button"
              tabIndex={0}
              className="flex items-center justify-between w-full text-left cursor-pointer"
              onClick={handleToggleArticlesPanel}
              onKeyDown={(e) => e.key === 'Enter' && handleToggleArticlesPanel()}
              data-testid="nexus-articles-curation-toggle"
            >
              <CardTitle className="text-base flex items-center gap-2">
                <Rss className="w-4 h-4" />
                Article Curation
                <Badge variant="secondary" className="text-xs ml-1">
                  {articles.filter(a => !a.hidden).length} visible
                </Badge>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); fetchArticles(); }}
                  data-testid="nexus-articles-refresh-btn" disabled={articlesLoading}>
                  <RefreshCw className={`w-3 h-3 ${articlesLoading ? 'animate-spin' : ''}`} />
                </Button>
                {articlesOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
            <CardDescription className="text-xs mt-1">
              Star articles to feature them at the top. Hide articles to remove them from the PWA.
            </CardDescription>
          </CardHeader>
          {articlesOpen && (
            <CardContent className="space-y-2">
              {articlesLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : articles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No articles found in Nexus. Make sure your Tenant ID and API key are correct.
                </p>
              ) : (
                articles.map(article => (
                  <ArticleCurationRow
                    key={article.id}
                    article={article}
                    onToggle={handleCurate}
                  />
                ))
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
